/* eslint-disable @typescript-eslint/no-explicit-any */
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import * as Sentry from "@sentry/vue";
import { getAuth } from "firebase/auth";
import { useUserProfileStore } from "@/store/UserProfile";
import { useManageEventStore } from "@/store/ManageEvent";
import { useAppStore } from "@/store/App";
import SystemDataService from "@/services/SystemDataService";
import Home from "@/views/Home.vue";
import PastEvent from "@/views/PastEvent.vue";
import PastEvents from "@/views/PastEvents.vue";
import Login from "@/views/Login.vue";
import AcesEvent from "@/views/AcesEvent.vue";
import YourEvents from "@/views/YourEvents.vue";
import MyHostedEvents from "@/views/MyHostedEvents.vue";
import Register from "@/views/Register.vue";
import EventAddon from "@/views/EventAddon.vue";
import RegisteredEvent from "@/views/RegisteredEvent.vue";
import PaymentConfirmation from "@/views/PaymentConfirmation.vue";
import CheckIn from "@/views/CheckIn.vue";
import EventReceipt from "@/views/EventReceipt.vue";
import Leaderboard from "@/views/Leaderboard.vue";
import { System } from "@models/System";
import { remoteConfig } from "@/firebase";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import CompleteRegistration from "@/views/CompleteRegistration.vue";
import ManageHands from "@/views/ManageHands.vue";

let systemData: System | null = null;

const shouldAcceptUpdatedTerms = async (profile: any) => {
  if (!profile) return false;

  if (!systemData) {
    systemData = await SystemDataService.getActive();
  }

  // Only check for updated terms, not missing profile data
  return (
    systemData !== null &&
    (!profile.acceptedPrivacyPolicy ||
      !profile.acceptedTermsOfUse ||
      !profile.acceptedPrivacyPolicyDate ||
      !profile.acceptedTermsOfUseDate ||
      systemData?.privacyPolicyEffectiveDate > profile.acceptedPrivacyPolicyDate ||
      systemData?.termsOfUseEffectiveDate > profile.acceptedTermsOfUseDate)
  );
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: {
      allowBrowser: true,
      page: "Home",
      parent: "Home",
      menu: [
        {
          route: "ManageNewEvent",
          title: "New Event",
        },
        {
          route: "PastEvents",
          title: "Past Events",
        },
      ],
    },
  },
  {
    path: "/PastEvents",
    name: "PastEvents",
    component: PastEvents,
    meta: {
      page: "Past Events",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/PastEvent/:id",
    name: "PastEvent",
    component: PastEvent,
    meta: {
      page: "Event",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/YourEvents",
    name: "YourEvents",
    component: YourEvents,
    meta: {
      page: "Your Events",
      parent: "YourEvents",
      menu: [
        {
          route: "ManageNewEvent",
          title: "New Event",
        },
      ],
    },
  },
  {
    path: "/MyHostedEvents",
    name: "MyHostedEvents",
    component: MyHostedEvents,
    meta: {
      page: "My Hosted Events",
      parent: "MyHostedEvents",
      menu: [
        {
          route: "ManageNewEvent",
          title: "New Event",
        },
      ],
    },
  },
  {
    path: "/AcesEvent/:id",
    name: "AcesEvent",
    component: AcesEvent,
    meta: {
      page: "Event",
      parent: "Home",
      allow_guest: true,
      actions: { share: true, back: true },
      allowBrowser: false,
    },
  },
  {
    path: "/EventReceipt",
    name: "EventReceipt",
    component: EventReceipt,
    meta: {
      page: "Receipt",
      parent: "YourEvents",
      actions: { back: true },
      allowBrowser: false,
    },
  },
  {
    path: "/EventAddons/:id",
    name: "EventAddons",
    component: EventAddon,
    meta: {
      page: "Event Add-ons",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/Register/:id",
    name: "Register",
    component: Register,
    meta: {
      page: "Register",
      parent: "Home",
      actions: { back: true },
      allowBrowser: false,
    },
  },
  {
    path: "/RegisteredEvent/:id",
    name: "RegisteredEvent",
    component: RegisteredEvent,
    meta: {
      page: "Event",
      backPage: "YourEvents",
      parent: "YourEvents",
      actions: { back: true },
      menu: [
        {
          route: "AcesEvent",
          title: "Event Details",
        },
        {
          route: "EventReceipt",
          title: "Receipt",
        },
        {
          route: "Leaderboard",
          title: "Leaderboard",
        },
      ],
    },
  },
  {
    path: "/PaymentConfirmation/:id/:transactionId",
    name: "PaymentConfirmation",
    component: PaymentConfirmation,
    meta: {
      page: "Confirmation",
      backPage: "YourEvents",
      parent: "YourEvents",
      allowBrowser: false,
    },
  },
  {
    path: "/Login",
    name: "Login",
    component: Login,
    meta: {
      allow_guest: true,
      hideNav: true,
      hideTopNav: true,
      allowBrowser: true,
    },
  },
  {
    path: "/verify-email",
    name: "VerifyEmail",
    component: () => import(/* webpackChunkName: 'SignUp' */ "@/views/VerifyEmail.vue"),
    meta: {
      allow_guest: false,
      hideNav: true,
      hideTopNav: true,
      allowBrowser: true,
    },
  },
  {
    path: "/SignUp",
    name: "SignUp",
    component: () => import(/* webpackChunkName: 'SignUp' */ "@/views/SignUp.vue"),
    meta: {
      page: "Sign Up",
      hideNav: true,
      hideTopNav: true,
      allowBrowser: true,
    },
  },
  {
    path: "/AcceptUpdatedTerms",
    name: "AcceptUpdatedTerms",
    component: () => import(/* webpackChunkName: 'AcceptUpdatedTerms' */ "@/views/AcceptUpdatedTerms.vue"),
    meta: {
      page: "Accept Updated Terms",
      hideNav: true,
      hideTopNav: true,
      allowBrowser: true,
    },
  },
  {
    path: "/SignOut",
    name: "SignOut",
    component: () => import(/* webpackChunkName: 'SignUp' */ "@/views/SignOut.vue"),
    meta: {
      page: "Sign Out",
      hideNav: true,
      hideTopNav: true,
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: 'about' */ "@/views/About.vue"),
    meta: {
      page: "About",
      parent: "Home",
      allow_guest: true,
      allowBrowser: true,
      isLite: true,
      actions: { back: true },
    },
  },
  {
    path: "/RegisteredEvent/:id/CheckIn/:code?",
    name: "CheckIn",
    component: CheckIn,
    meta: {
      page: "Get Your Card",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/CheckInCodes",
    name: "CheckInCodes",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/CheckInCodes.vue"),
    meta: {
      page: "Check In Codes",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/CheckInAllCode",
    name: "CheckInAllCode",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/CheckInAllCode.vue"),
    meta: {
      page: "Check In All Code",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/EventCode",
    name: "EventCode",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/EventCode.vue"),
    meta: {
      page: "Event Code",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/Leaderboard/:id?",
    name: "Leaderboard",
    component: Leaderboard,
    meta: {
      page: "Leaderboard",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/AssignRegistrations/:eventId",
    name: "AssignRegistrations",
    component: () =>
      import(/* webpackChunkName: 'AssignRegistrations' */ "@/views/AssignRegistrations.vue"),
    meta: {
      page: "Assign Registrations",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/Redeem/:eventId/:redeemCode",
    name: "Redeem",
    component: () => import(/* webpackChunkName: 'Redeem' */ "@/views/Redeem.vue"),
    meta: {
      page: "Redeem",
      parent: "Home",
      allow_guest: true,
      transfer: false,
      doNotRedirect: true,
    },
    beforeEnter: (to, from, next) => {
      const { isUserAuthenticated } = useUserProfileStore();
      if (isUserAuthenticated) {
        next({
          name: "RedeemEvent",
          params: { eventId: to.params.eventId, redeemCode: to.params.redeemCode, transfer: false },
        });
      } else {
        next();
      }
    },
  },
  {
    path: "/Transfer/:eventId/:redeemCode",
    name: "Transfer",
    component: () => import(/* webpackChunkName: 'Redeem' */ "@/views/Redeem.vue"),
    meta: {
      page: "Redeem",
      parent: "Home",
      allow_guest: true,
      transfer: true,
      doNotRedirect: true,
    },
    beforeEnter: (to, from, next) => {
      const { isUserAuthenticated } = useUserProfileStore();
      if (isUserAuthenticated) {
        next({
          name: "RedeemEvent",
          params: { eventId: to.params.eventId, redeemCode: to.params.redeemCode, transfer: true },
        });
      } else {
        next();
      }
    },
  },
  {
    path: "/RedeemEvent/:eventId/:redeemCode/:transfer",
    name: "RedeemEvent",
    component: () => import(/* webpackChunkName: 'Redeem' */ "@/views/RedeemEvent.vue"),
    meta: {
      page: "Redeem",
      parent: "Home",
      doNotRedirect: true,
    },
  },
  {
    path: "/JoinLanding/:eventId/:code",
    name: "ManagerJoinLanding",
    component: () => import(/* webpackChunkName: 'Redeem' */ "@/views/ManagerJoinLanding.vue"),
    meta: {
      page: "Join",
      parent: "Home",
      hideNav: true,
      allow_guest: true,
      doNotRedirect: true,
    },
    beforeEnter: (to, from, next) => {
      const { isUserAuthenticated } = useUserProfileStore();
      if (isUserAuthenticated) {
        next({
          name: "ManagerJoin",
          params: { eventId: to.params.eventId, code: to.params.code },
        });
      } else {
        next();
      }
    },
  },
  {
    path: "/Join/:eventId/:code",
    name: "ManagerJoin",
    component: () => import(/* webpackChunkName: 'Redeem' */ "@/views/ManagerJoin.vue"),
    meta: {
      parent: "Home",
      page: "Join",
      doNotRedirect: true,
    },
  },
  {
    path: "/AssignRegistration/:eventId/:id",
    name: "AssignRegistration",
    component: () =>
      import(/* webpackChunkName: 'AssignRegistrations' */ "@/views/AssignRegistration.vue"),
    meta: {
      page: "Assign Registration",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/CancelRegistrationAssignment/:eventId/:id",
    name: "CancelRegistrationAssignment",
    component: () =>
      import(
        /* webpackChunkName: 'AssignRegistrations' */ "@/views/CancelRegistrationAssignment.vue"
      ),
    meta: {
      page: "Cancel Registration",
      parent: "YourEvents",
      actions: { back: true },
    },
  },
  {
    path: "/Participants",
    name: "Participants",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/Participants.vue"),
    meta: {
      page: "Participants",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/Transactions",
    name: "Transactions",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/Transactions.vue"),
    meta: {
      page: "Transactions",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/ManageEventInfo",
    name: "ManageEventInfo",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventInfo.vue"),
    meta: {
      page: "Manage Event",
      actions: { back: true },
      isSetupFlow: true,
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageNewEvent",
    name: "ManageNewEvent",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventInfo.vue"),
    beforeEnter: (to, from, next) => {
      const eventStore = useManageEventStore();
      eventStore.$reset();
      next({ name: "ManageEventInfo" });
    },
  },
  {
    path: "/ManageEventStops",
    name: "ManageEventStops",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventStops.vue"),
    meta: {
      page: "Manage Stops",
      actions: { back: true },
      isSetupFlow: true,
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventManager/:id?",
    name: "ManageEventManager",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventManager.vue"),
    meta: {
      page: "Event Manager",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventManagers",
      saveRoute: "ManageEventManagers"
    },
  },
  {
    path: "/ManageEventManagers",
    name: "ManageEventManagers",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventManagers.vue"),
    meta: {
      page: "Event Managers",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    }
  },
  {
    path: "/ManageEventHands",
    name: "ManageEventHands",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventHands.vue"),
    meta: {
      page: "Event Hands",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    }
  },
  {
    path: "/ManageEventNewHand",
    name: "ManageEventNewHand",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventNewHand.vue"),
    meta: {
      page: "Create Hand",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventHands",
      saveRoute: "ManageEventHands"
    }
  },
  {
    path: "/ManageEventTracker/:id?",
    name: "ManageEventTracker",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventTracker.vue"),
    meta: {
      page: "Manage Tracker",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventTrackers",
      saveRoute: "ManageEventTrackers"
    },
  },
  {
    path: "/TrackerReport/:id?",
    name: "TrackerReport",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/TrackerReport.vue"),
    meta: {
      page: "Tracker Report",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    }
  },
  {
    path: "/ManageEventTrackers",
    name: "ManageEventTrackers",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventTrackers.vue"),
    meta: {
      page: "Manage Trackers",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventConnectedAccount",
    name: "ManageEventConnectedAccount",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventConnectedAccount.vue"),
    meta: {
      page: "Financial Setup",
      actions: { back: true },
      parent: "MyHostedEvents",      
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventPayments",
    name: "ManageEventPayments",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventPayments.vue"),
    meta: {
      page: "Payments",
      actions: { back: true },      
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventFees",
    name: "ManageEventFees",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventFees.vue"),
    meta: {
      page: "Fees",
      actions: { back: true },
      isSetupFlow: true,
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventCard",
    name: "ManageEventCard",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventCard.vue"),
    meta: {
      page: "Card Setup",
      actions: { back: true },
      isSetupFlow: true,
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventConfirmation",
    name: "ManageEventConfirmation",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventConfirmation.vue"),
    meta: {
      page: "Event Created",
      actions: { back: true },
      isSetupFlow: false,
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventPublished"
    },
  },
  {
    path: "/ManageEventSummary/:id?",
    name: "ManageEventSummary",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventSummary.vue"),
    meta: {
      page: "Event Summary",
      parent: "MyHostedEvents",
      actions: { back: true },
      menu: [
        {
          route: "CheckInCodes",
          title: "Check In Codes",
        },
        {
          route: "EventCode",
          title: "Event Code",
        },
      ],
      cancelRoute: "MyHostedEvents",
      saveRoute: "MyHostedEvents"
    },
  },
  {
    path: "/ManageEventSummary/:id/account",
    name: "ManageEventConnectedAccountConfirmation",
    component: () =>
      import(
        /* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventConnectedAccountConfirmation.vue"
      ),
    meta: {
      page: "Financial Setup Confirmation",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventSummary/:id/account/refresh",
    name: "ManageEventConnectedAccountRefresh",
    component: () =>
      import(
        /* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventConnectedAccountRefresh.vue"
      ),
    meta: {
      page: "Financial Setup",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventLineItems",
    name: "ManageEventLineItems",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventLineItems.vue"),
    meta: {
      page: "Manage Add-Ons",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventPublished/:id?",
    name: "ManageEventPublished",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventPublished.vue"),
    meta: {
      page: "Publish",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "MyHostedEvents",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventUnpublish/:id?",
    name: "ManageEventUnpublish",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventUnpublish.vue"),
    meta: {
      page: "Unpublish",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventClose/:id?",
    name: "ManageEventClose",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventClose.vue"),
    meta: {
      page: "Close Event",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventDealRemaingCards/:id?",
    name: "ManageEventDealRemaingCards",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventDealRemaingCards.vue"),
    meta: {
      page: "Deal Remaining Cards",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventRegistrationClose/:id?",
    name: "ManageEventRegistrationClose",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventRegistrationClose.vue"),
    meta: {
      page: "Close Registration",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventRegistrationOpen/:id?",
    name: "ManageEventRegistrationOpen",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventRegistrationOpen.vue"),
    meta: {
      page: "Open Registration",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ManageEventWaiver",
    name: "ManageEventWaiver",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventWaiver.vue"),
    meta: {
      page: "Manage Waiver",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },

  {
    path: "/ManageEventCompletePurchase",
    name: "ManageEventCompletePurchase",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ManageEventCompletePurchase.vue"),
    meta: {
      page: "Manage Purchase",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/ExtraCardCashPurchase",
    name: "ExtraCardCashPurchase",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/ExtraCardCashPurchase.vue"),
    meta: {
      page: "Extra Card Cash Purchase",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/TradeShowReport",
    name: "TradeShowReport",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/TradeShowReport.vue"),
    meta: {
      page: "Trade Show Report",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/TrackEvent/:id",
    name: "TrackEvent",
    component: () => import(/* webpackChunkName: 'ManageEvent' */ "@/views/TrackEvent.vue"),
    meta: {
      page: "Track",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventSummary",
      saveRoute: "ManageEventSummary"
    },
  },
  {
    path: "/TransferRegistration/:registrationId",
    name: "TransferRegistration",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/TransferRegistration.vue"),
    meta: {
      page: "Transfer Registration",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
  {
    path: "/TransferRegistrationCancel/:registrationId",
    name: "TransferRegistrationCancel",
    component: () =>
      import(/* webpackChunkName: 'ManageEvent' */ "@/views/TransferRegistrationCancel.vue"),
    meta: {
      page: "Cancel Transfer",
      actions: { back: true },
      parent: "MyHostedEvents",
    },
  },
  {
    path: "/Profile",
    name: "Profile",
    component: () => import(/* webpackChunkName: 'Profile' */ "@/views/Profile.vue"),
    meta: {
      page: "Profile",
      actions: { back: true },
      parent: "Home",
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/VerifyConfirmationCode",
    name: "VerifyConfirmationCode",
    component: () => import(/* webpackChunkName: 'Profile' */ "@/views/VerifyConfirmationCode.vue"),
    meta: {
      page: "Verify Phone Number",
      actions: { back: true },
      parent: "Home",
      allowBrowser: true,
      isLite: true,
      hideNav: true,
      hideTopNav: true,
    },
  },
  {
    path: "/MyQRCode",
    name: "MyQRCode",
    component: () => import(/* webpackChunkName: 'Profile' */ "@/views/MyQRCode.vue"),
    meta: {
      page: "My QR Code",
      actions: { back: true },
      parent: "Home",
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/DeleteAccount",
    name: "DeleteAccount",
    component: () => import(/* webpackChunkName: 'DeleteAccount' */ "@/views/DeleteAccount.vue"),
    meta: {
      page: "Delete Account",
      parent: "Home",
      actions: { back: true },
    },
  },
  {
    path: "/Action",
    name: "Action",
    component: () => import(/* webpackChunkName: 'Action' */ "@/views/Action.vue"),
    meta: {
      page: "Action",
      actions: { back: true },
      allow_guest: true,
      hideNav: true,
      hideTopNav: true,
    },
  },
  {
    path: "/Lite/EventSignUp/:id",
    name: "EventSignUp",
    component: () => import(/* webpackChunkName: 'Lite' */ "@/views/EventSignUp.vue"),
    meta: {
      page: "AcesPokerRun.com",
      actions: { back: false },
      allow_guest: true,
      hideNav: true,
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/Lite/RegisteredEvent/:id",
    name: "RegisteredEventLite",
    component: () => import(/* webpackChunkName: 'Lite' */ "@/views/RegisteredEventLite.vue"),
    meta: {
      page: "AcesPokerRun.com",
      actions: { back: false },
      allow_guest: false,
      hideNav: true,
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/Lite/RegisteredEvent/:id/CheckIn/:code?",
    name: "CheckInLite",
    component: CheckIn,
    meta: {
      page: "AcesPokerRun.com",
      actions: { back: true },
      hideNav: true,
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: "/Lite/Leaderboard/:id",
    name: "LeaderboardLite",
    component: () => import(/* webpackChunkName: 'Lite' */ "@/views/LeaderboardLite.vue"),
    meta: {
      page: "AcesPokerRun.com",
      actions: { back: true },
      allow_guest: false,
      hideNav: true,
      allowBrowser: true,
      isLite: true,
    },
  },
  {
    path: '/manage-event/:id/ReorderStops',
    name: 'ReorderStops',
    component: () => import('@/views/ManageEventStopsReorder.vue'),
    meta: {
      requiresAuth: true,
      cancelRoute: "ManageEventStops",
      parent: "MyHostedEvents",
      saveRoute: "ManageEventStops"
    }
  },
  {
    path: '/manage/event/stops/:order/edit',
    name: 'ManageEventStopEdit',
    component: () => import('@/views/ManageEventStopEdit.vue'),
    meta: {
      requiresAuth: true,
      layout: 'manage',
      page: "Edit Stop",
      actions: { back: true },
      parent: "MyHostedEvents",
      cancelRoute: "ManageEventStops",
      saveRoute: "ManageEventStops",
      isSetupFlow: true
    }
  },
  {
    path: "/register2/:id",
    name: "CompleteRegistration",
    component: CompleteRegistration,
    meta: {
      requiresAuth: true,
      parent: "YourEvents",
      requiresAcceptance: true,
    },
  },
  {
    path: "/ManageHands",
    name: "ManageHands",
    component: ManageHands,
    meta: {
      page: "Manage Hands",
      parent: "MyHostedEvents",
      actions: { back: true },
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.afterEach(async () => {
  if (
    Date.now() - remoteConfig.fetchTimeMillis >
    remoteConfig.settings.minimumFetchIntervalMillis
  ) {
    fetchAndActivate(remoteConfig)
      .then(() => {
        console.log("config fetched and activated");
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }
});

router.beforeEach(async (to, from, next) => {
  if (to.query.standalone) {
    useAppStore().setStandalone(true);
  }

  if (to.meta && !to.meta.allow_guest) {
    const auth = getAuth();
    const userProfileStore = useUserProfileStore();

    if (auth.currentUser) {
      userProfileStore.setIsAuthenticated(true);

      if (to.name === "SignOut" ||
          to.name === "AcceptUpdatedTerms" ||
          to.name === "SignUp" ||
          to.name === "VerifyConfirmationCode" 
      ) {
        next();
        return;
      }

      if (!userProfileStore.isInitialized && navigator.onLine) {
        await userProfileStore.syncProfileWithDb();
      }

      // Check if user needs to accept updated terms (separate from initial signup)
      const needsUpdatedTerms = await shouldAcceptUpdatedTerms(userProfileStore.profile);
      if (
        needsUpdatedTerms &&
        navigator.onLine
      ) {
        next({ name: "AcceptUpdatedTerms" });
        return;
      }

      if (
        (!userProfileStore.profile.phoneNumber || !userProfileStore.profile.phoneNumberVerified) 
      ) {
        next({ name: "SignUp" });
        return;
      }

      if (userProfileStore.verifyPhone) {
        if (to.name !== "SignUp" && to.name !== "VerifyConfirmationCode") {
          next({ name: "VerifyConfirmationCode" });
          return;
        }

        next();
        return;
      }

      if (
        to.name !== "VerifyEmail" &&
        userProfileStore.verifyEmail &&
        !userProfileStore.profile.sentVerifyEmail &&
        navigator.onLine
      ) {
        next({ name: "VerifyEmail" });
        return;
      } else if (to.name === "VerifyEmail") {
        next();
        return;
      }
      
      if (to.name !== "SignUp" && to.name !== "AcceptUpdatedTerms") {
        userProfileStore.setRedirectUrl("");
      }
      
      next();
      
    } else {
      if (to.name !== "Login") {
        userProfileStore.setRedirectUrl(to.fullPath);
      }

      if (to.meta.isLite) {
        next({ name: "EventSignUp", params: { id: to.params.id }, query: to.query });
      } else {
        next({ name: "Login" });
      }
    }
  } else {
    next();
  }
});

export default router;
